import { useEffect, useRef } from 'react';

/* ==================================================================
   usePointerVars
   ------------------------------------------------------------------
   Writes the pointer's position as two CSS custom properties on a
   container: --hs-mx / --hs-my in the range -1 -> 1 (0,0 = centre),
   plus --hs-px / --hs-py as 0 -> 1 for spotlight gradients.

   Children then opt in to as much or as little of that as they like
   via calc(), and a CSS transition on the child does the easing, so
   there is no JS lerp loop, no React state, and the values glide
   smoothly back to centre when the pointer leaves.
   ================================================================== */

export default function usePointerVars(options) {
  const { disabled = false, global = false } = options || {};
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || disabled) return undefined;

    const target = global ? window : node;
    let frame = 0;
    let pending = null;

    const write = () => {
      frame = 0;
      if (!pending) return;
      const { clientX, clientY } = pending;
      pending = null;

      const rect = global
        ? { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
        : node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const px = (clientX - rect.left) / rect.width;
      const py = (clientY - rect.top) / rect.height;

      node.style.setProperty('--hs-px', px.toFixed(4));
      node.style.setProperty('--hs-py', py.toFixed(4));
      node.style.setProperty('--hs-mx', (px * 2 - 1).toFixed(4));
      node.style.setProperty('--hs-my', (py * 2 - 1).toFixed(4));
    };

    const onMove = (event) => {
      pending = { clientX: event.clientX, clientY: event.clientY };
      if (!frame) frame = requestAnimationFrame(write);
    };

    const onLeave = () => {
      pending = null;
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      node.style.setProperty('--hs-mx', '0');
      node.style.setProperty('--hs-my', '0');
      node.style.setProperty('--hs-px', '0.5');
      node.style.setProperty('--hs-py', '0.5');
    };

    target.addEventListener('pointermove', onMove, { passive: true });
    if (!global) node.addEventListener('pointerleave', onLeave, { passive: true });

    return () => {
      target.removeEventListener('pointermove', onMove);
      if (!global) node.removeEventListener('pointerleave', onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [disabled, global]);

  return ref;
}
